<template>
    <component :is="tag"
               :alt="alt"
               v-bind="imageSrc"
               class="wpimage"
               :class="[imageClass, classes]"
               :width="imageWidth"
               v-observe-visibility="{
                    callback: visibilityChanged,
                    throttle: throttle,
                    intersection: {
                        rootMargin: rootMargin,
                        threshold: threshold
                    }
               }">
        <slot />
    </component>
</template>

<script>
    import _get from 'lodash/get'
    import { wpMediaApi } from '@/utils/api'
    import inViewport from 'vue-in-viewport-mixin'

    export default {
        name: "WpImage",
        mixins: [ inViewport ],
        props: {
            image: {
                type: Boolean | Object | Number,
                required: true,
                default: false
            },
            sizeSd: {
                type: String,
                default: 'sd'
            },
            sizeHd: {
                type: String,
                default: 'hd'
            },
            isBackground: {
                type: Boolean,
                default: false
            },
            loadImmediate: {
                type: Boolean,
                default: false
            },
            throttle: {
                type: Number,
                default: 100
            },
            rootMargin: {
                type: String,
                default: '0px 0px 200px 0px'
            },
            threshold: {
                type: Number,
                default: 0
            },
            classes: {
                type: String,
                default: "",
            },
            width: {
                type: [Boolean, Number],
                default: false,
            },
        },
        data() {
            return {
                loaded: false,
                loadedUrl: null,
                isVisible: false,
                visible: false,
                isAbove: false,
                isBelow: false,
            }
        },
        mounted() {
            if (this.loadImmediate) this.loadHDImage();
            if (window.innerWidth < 500) this.loadHDImage();
        },
        asyncComputed: {
            async url() {
                if (!this.image) return null;

                const urlSD = _get(this.image, `sizes.${this.sizeSd}`, this.image.url) || this.image.url;

                return urlSD
            },
            async urlHD() {
                if (!this.image) return null;

                let urlHD = this.image.url || '';
                if (this.image && this.sizeHd !== 'full') {
                    urlHD = _get(this.image,  `sizes.${this.sizeHd}`, this.image.url)
                }

                await this.loadImage(urlHD);

                this.$emit('load');

                return urlHD
            }
        },
        computed: {
            tag() {
                return this.isBackground ? 'div':'img'
            },
            imageWidth(){
                if (this.width && this.tag === 'img') {
                    return this.width;
                } return "";
            },
            alt() {
                return this.image ? this.image.title:''
            },
            imageClass() {
                return this.loaded ? 'loaded':'loading';
            },
            imageSrc() {
                const url = this.loaded && this.urlHD ? this.urlHD : this.url;

                if (!url) return {};
                return this.isBackground ? { style: { backgroundImage: `url(${url})` } } : { src: url };
            },
        },
        methods: {
            loadImage(src) {
                return new Promise((resolve, reject) => {
                    const image = new Image();
                    image.onload = resolve;
                    image.onerror = reject;
                    image.src = src;
                })
            },
            loadHDImage() {
                this.loaded = true;
                this.$emit('loaded');
            },
            visibilityChanged(isVisible, element) {
                this.isVisible = isVisible;
                if (isVisible && !this.loaded) this.loadHDImage();
            },
            async getImageData() {
                return this.image = await wpMediaApi.get(`${this.image}`).then(res => res.data);
            },
            isLoaded(e){
                this.$emit('isLoaded', e);
            },
        },
        watch: {
            image: {
                immediate: true,
                handler(image, oldImage) {
                    if (typeof image === 'number') {
                        this.getImageData();
                    }
                }
            }
        }
    }
</script>

<style lang="scss">
    .wpimage.loading {
        filter: blur(3px);
        transition: filter 0.3s;
        position: relative;
    }
</style>